import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
    CalendarDays,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    CircleAlert,
    CircleCheck,
    CircleX,
    Hourglass,
    Loader2,
    Play,
    RefreshCw,
    RotateCcw,
    Search,
    Timer,
    Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { PageContainer } from "../../../common/components/PageContainer";
import { Toast } from "../../../components/design-system/common";
import { useProductsApi } from "../../../common/hooks/use-products-api.hook";

interface BatchSummary {
    _id: string;
    totalJobs: number;
    pending: number;
    processing: number;
    generating_seamless: number;
    completed: number;
    failed: number;
    createdAt: string;
    completedAt: string;
    status: 'in_progress' | 'success' | 'partial' | 'failed';
    sampleProducts: string[];
}

interface BatchHistoryResponse {
    batches: BatchSummary[];
    totalBatches: number;
}

interface BatchDetails {
    batchId: string;
    summary: {
        total: number;
        pending: number;
        processing: number;
        generating_seamless: number;
        completed: number;
        failed: number;
    };
    jobs: Array<{
        jobId: string;
        operation: string;
        status: string;
        productData?: {
            name?: string;
            id?: string;
            sizeName?: string;
            pattern?: string;
            color?: string;
            width?: number;
            length?: number;
        };
        resultProduct?: {
            _id: string;
            uuid: string;
            name: string;
            thumbnailPath?: string;
            fullPath?: string;
        };
        error?: string;
        errorDetails?: { userMessage?: string; suggestion?: string };
        createdAt: string;
        completedAt?: string;
    }>;
}

interface DayGroup {
    date: string;
    dateLabel: string;
    batches: BatchSummary[];
    totalProducts: number;
    totalSuccess: number;
    totalFailed: number;
}

// Status is the one place the palette allows a hue beyond the neutrals.
const statusTone: Record<string, string> = {
    pending: 'text-warning',
    processing: 'text-foreground',
    generating_seamless: 'text-foreground',
    completed: 'text-success',
    failed: 'text-destructive',
    in_progress: 'text-foreground',
    success: 'text-success',
    partial: 'text-warning',
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const headCls = "text-muted-foreground";

/** The success/failure tally reads the same in the day header and in every batch row. */
const TallyBadge = ({ kind, count }: { kind: 'success' | 'failed'; count: number }) => {
    const Icon = kind === 'success' ? CircleCheck : CircleAlert;
    const tone = count > 0
        ? (kind === 'success' ? 'text-success' : 'text-destructive')
        : 'text-muted-foreground';
    return (
        <Badge variant="outline" className={cn('gap-1', tone)}>
            <Icon />
            {count}
        </Badge>
    );
};

export function ImportHistoryPage() {
    const { t, i18n } = useTranslation();
    const [batches, setBatches] = useState<BatchSummary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [errorToastVisible, setErrorToastVisible] = useState<boolean>(false);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [expandedBatch, setExpandedBatch] = useState<string | null>(null);
    const [expandedDay, setExpandedDay] = useState<string | null>(null);
    const [batchDetails, setBatchDetails] = useState<Record<string, BatchDetails>>({});
    const [loadingDetails, setLoadingDetails] = useState<string | null>(null);
    const [hoursFilter, setHoursFilter] = useState<number>(168);
    const [confirmCancelBatch, setConfirmCancelBatch] = useState<string | null>(null);
    const [cancellingBatch, setCancellingBatch] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalBatches, setTotalBatches] = useState(0);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [retryingJob, setRetryingJob] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');

    const productsApi = useProductsApi();
    const productsApiRef = useRef(productsApi);
    productsApiRef.current = productsApi;

    const statusLabels: Record<string, string> = {
        in_progress: t('PAGES.IMPORT_HISTORY.STATUS_IN_PROGRESS'),
        success: t('PAGES.IMPORT_HISTORY.STATUS_SUCCESS'),
        partial: t('PAGES.IMPORT_HISTORY.STATUS_PARTIAL'),
        failed: t('PAGES.IMPORT_HISTORY.STATUS_FAILED'),
    };

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Group batches by day
    const groupedByDay = useMemo((): DayGroup[] => {
        const groups: Record<string, DayGroup> = {};

        batches.forEach(batch => {
            const date = new Date(batch.createdAt);
            const dateKey = date.toISOString().split('T')[0];

            if (!groups[dateKey]) {
                const isToday = new Date().toISOString().split('T')[0] === dateKey;
                const isYesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0] === dateKey;

                let dateLabel = date.toLocaleDateString(i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'es' ? 'es-ES' : 'en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                });

                if (isToday) dateLabel = t('PAGES.IMPORT_HISTORY.TODAY');
                else if (isYesterday) dateLabel = t('PAGES.IMPORT_HISTORY.YESTERDAY');

                groups[dateKey] = {
                    date: dateKey,
                    dateLabel,
                    batches: [],
                    totalProducts: 0,
                    totalSuccess: 0,
                    totalFailed: 0,
                };
            }

            groups[dateKey].batches.push(batch);
            groups[dateKey].totalProducts += batch.totalJobs;
            groups[dateKey].totalSuccess += batch.completed;
            groups[dateKey].totalFailed += batch.failed;
        });

        return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
    }, [batches, t, i18n.language]);

    const fetchBatchHistory = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const skip = page * rowsPerPage;
            const data: BatchHistoryResponse = await productsApiRef.current.getBatchHistory(hoursFilter, rowsPerPage, skip, statusFilter, debouncedSearch);
            setBatches(data.batches || []);
            setTotalBatches(data.totalBatches || 0);
            setLastRefresh(new Date());
            if (page === 0) {
                const today = new Date().toISOString().split('T')[0];
                if (data.batches?.some(b => b.createdAt.startsWith(today))) {
                    setExpandedDay(today);
                }
            }
        } catch (err: any) {
            setError(t('PAGES.IMPORT_HISTORY.ERROR_LOADING'));
            setErrorToastVisible(true);
            console.error('Error fetching batch history:', err);
        } finally {
            setIsLoading(false);
        }
    }, [hoursFilter, page, rowsPerPage, statusFilter, debouncedSearch, t]);

    useEffect(() => {
        fetchBatchHistory();
    }, [fetchBatchHistory]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRetryJob = async (jobId: string, batchId: string) => {
        setRetryingJob(jobId);
        try {
            await productsApi.retryBulkJob(jobId);
            setBatchDetails(prev => {
                if (!prev[batchId]) return prev;
                return {
                    ...prev,
                    [batchId]: {
                        ...prev[batchId],
                        jobs: prev[batchId].jobs.map(job =>
                            job.jobId === jobId
                                ? { ...job, status: 'pending', error: undefined, errorDetails: undefined }
                                : job
                        )
                    }
                };
            });
            pollJobStatus(jobId, batchId);
        } catch (err) {
            console.error('Error retrying job:', err);
            setError(t('PAGES.IMPORT_HISTORY.ERROR_RETRY'));
            setErrorToastVisible(true);
            setRetryingJob(null);
        }
    };

    const pollJobStatus = async (jobId: string, batchId: string) => {
        const maxAttempts = 60;
        const pollInterval = 2000;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            try {
                const details = await productsApi.getBulkBatchStatus(batchId);
                setBatchDetails(prev => ({ ...prev, [batchId]: details }));
                const job = details.jobs.find((j: any) => j.jobId === jobId);
                if (job && (job.status === 'completed' || job.status === 'failed')) {
                    setRetryingJob(null);
                    await fetchBatchHistory();
                    return;
                }
            } catch (err) {
                console.error('Error polling job status:', err);
            }
        }
        setRetryingJob(null);
    };

    const fetchBatchDetails = async (batchId: string) => {
        if (batchDetails[batchId]) return;
        setLoadingDetails(batchId);
        try {
            const details = await productsApi.getBulkBatchStatus(batchId);
            setBatchDetails(prev => ({ ...prev, [batchId]: details }));
        } catch (err) {
            console.error('Error fetching batch details:', err);
        } finally {
            setLoadingDetails(null);
        }
    };

    const handleExpandBatch = (batchId: string) => {
        if (expandedBatch === batchId) {
            setExpandedBatch(null);
        } else {
            setExpandedBatch(batchId);
            fetchBatchDetails(batchId);
        }
    };

    const handleExpandDay = (dateKey: string) => {
        setExpandedDay(expandedDay === dateKey ? null : dateKey);
    };

    const handleCancelBatch = async () => {
        if (!confirmCancelBatch) return;
        const batchId = confirmCancelBatch;
        setConfirmCancelBatch(null);
        setCancellingBatch(batchId);
        try {
            await productsApi.abortBulkBatch(batchId);
            setBatchDetails(prev => {
                const next = { ...prev };
                delete next[batchId];
                return next;
            });
            await fetchBatchHistory();
        } catch (err) {
            console.error('Error cancelling batch:', err);
            setError(t('PAGES.IMPORT_HISTORY.ERROR_CANCEL'));
            setErrorToastVisible(true);
        } finally {
            setCancellingBatch(null);
        }
    };

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString(i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'es' ? 'es-ES' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getProgressPercent = (batch: BatchSummary) => {
        if (batch.totalJobs === 0) return 0;
        return ((batch.completed + batch.failed) / batch.totalJobs) * 100;
    };

    const formatProductDetails = (job: BatchDetails['jobs'][0]) => {
        const parts: string[] = [];
        if (job.productData?.name) parts.push(job.productData.name);
        return parts.join(' - ') || t('PAGES.IMPORT_HISTORY.UNKNOWN_PRODUCT');
    };

    const getProductSubDetails = (job: BatchDetails['jobs'][0]) => {
        const details: string[] = [];
        if (job.productData?.sizeName) details.push(job.productData.sizeName);
        if (job.productData?.pattern) details.push(job.productData.pattern);
        if (job.productData?.color) details.push(job.productData.color);
        return details.join(' • ');
    };

    const firstRow = totalBatches === 0 ? 0 : page * rowsPerPage + 1;
    const lastRow = Math.min((page + 1) * rowsPerPage, totalBatches);
    const pageCount = Math.max(1, Math.ceil(totalBatches / rowsPerPage));

    return (
        <PageContainer breadcrumbs={[
            { label: t('SIDEBAR.PRODUCTS.TITLE') },
            { label: t('SIDEBAR.PRODUCTS.IMPORT_PRODUCTS'), icon: <Upload className="size-5" />, href: '/products/import' },
            { label: t('SIDEBAR.PRODUCTS.IMPORT_HISTORY') }
        ]}>
            {/* Toolbar */}
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative w-full sm:w-60">
                        <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            className="h-9 ps-8"
                            placeholder={t('PAGES.IMPORT_HISTORY.SEARCH_PLACEHOLDER')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <Separator orientation="vertical" className="hidden h-8 sm:block" />

                    <Select
                        value={String(hoursFilter)}
                        onValueChange={(value) => { setHoursFilter(Number(value)); setPage(0); }}
                    >
                        <SelectTrigger className="h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="24">{t('PAGES.IMPORT_HISTORY.LAST_24H')}</SelectItem>
                            <SelectItem value="168">{t('PAGES.IMPORT_HISTORY.LAST_7D')}</SelectItem>
                            <SelectItem value="720">{t('PAGES.IMPORT_HISTORY.LAST_30D')}</SelectItem>
                            <SelectItem value="2160">{t('PAGES.IMPORT_HISTORY.LAST_90D')}</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select
                        value={statusFilter}
                        onValueChange={(value) => { setStatusFilter(value); setPage(0); }}
                    >
                        <SelectTrigger className="h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('PAGES.IMPORT_HISTORY.ALL_STATUS')}</SelectItem>
                            <SelectItem value="in_progress">{t('PAGES.IMPORT_HISTORY.STATUS_IN_PROGRESS')}</SelectItem>
                            <SelectItem value="success">{t('PAGES.IMPORT_HISTORY.STATUS_SUCCESS')}</SelectItem>
                            <SelectItem value="partial">{t('PAGES.IMPORT_HISTORY.STATUS_PARTIAL')}</SelectItem>
                            <SelectItem value="failed">{t('PAGES.IMPORT_HISTORY.STATUS_FAILED')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                        {t('PAGES.IMPORT_HISTORY.UPDATED')} {lastRefresh.toLocaleTimeString()}
                    </span>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon-lg"
                                onClick={fetchBatchHistory}
                                disabled={isLoading}
                                aria-label={t('PAGES.IMPORT_HISTORY.REFRESH')}
                            >
                                <RefreshCw className={cn('size-4', isLoading && 'animate-spin')} />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('PAGES.IMPORT_HISTORY.REFRESH')}</TooltipContent>
                    </Tooltip>
                </div>
            </div>

            {error && (
                <Toast
                    message={error}
                    type="error"
                    isVisible={errorToastVisible}
                    onClose={() => setErrorToastVisible(false)}
                />
            )}

            {isLoading && batches.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="size-7 animate-spin text-muted-foreground" />
                </div>
            ) : batches.length === 0 ? (
                <div className="flex flex-col items-center rounded-xl border bg-card p-12 text-center">
                    <Timer className="mb-4 size-12 text-muted-foreground" strokeWidth={1.5} />
                    <p className="font-heading text-sm tracking-tight text-foreground">
                        {t('PAGES.IMPORT_HISTORY.NO_HISTORY')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t('PAGES.IMPORT_HISTORY.NO_HISTORY_HINT')}
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {groupedByDay.map((dayGroup) => (
                        <Collapsible
                            key={dayGroup.date}
                            open={expandedDay === dayGroup.date}
                            onOpenChange={() => handleExpandDay(dayGroup.date)}
                            className="rounded-xl border bg-card"
                        >
                            {/* Day Header */}
                            <CollapsibleTrigger
                                className={cn(
                                    'flex w-full items-center justify-between gap-4 p-4 text-start transition-colors hover:bg-muted/50',
                                    expandedDay === dayGroup.date && 'border-b bg-muted/50'
                                )}
                            >
                                <span className="flex items-center gap-3">
                                    <CalendarDays className="size-5 text-muted-foreground" />
                                    <span className="font-heading text-sm font-medium tracking-tight text-foreground">
                                        {dayGroup.dateLabel}
                                    </span>
                                    <Badge variant="outline" className="text-muted-foreground">
                                        {dayGroup.batches.length} {t('PAGES.IMPORT_HISTORY.IMPORTS')}
                                    </Badge>
                                </span>
                                <span className="flex items-center gap-3">
                                    <span className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">
                                            {dayGroup.totalProducts} {t('PAGES.IMPORT_HISTORY.PRODUCTS')}
                                        </span>
                                        {dayGroup.totalSuccess > 0 && <TallyBadge kind="success" count={dayGroup.totalSuccess} />}
                                        {dayGroup.totalFailed > 0 && <TallyBadge kind="failed" count={dayGroup.totalFailed} />}
                                    </span>
                                    {expandedDay === dayGroup.date
                                        ? <ChevronUp className="size-4 text-muted-foreground" />
                                        : <ChevronDown className="size-4 text-muted-foreground" />}
                                </span>
                            </CollapsibleTrigger>

                            {/* Day Content */}
                            <CollapsibleContent>
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-muted/50">
                                                <TableHead className="w-12" />
                                                <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.TIME')}</TableHead>
                                                <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.PRODUCTS')}</TableHead>
                                                <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.PROGRESS')}</TableHead>
                                                <TableHead className={cn(headCls, 'text-center')}>{t('PAGES.IMPORT_HISTORY.STATUS_SUCCESS')}</TableHead>
                                                <TableHead className={cn(headCls, 'text-center')}>{t('PAGES.IMPORT_HISTORY.STATUS_FAILED')}</TableHead>
                                                <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.STATUS')}</TableHead>
                                                <TableHead className="w-16" />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {dayGroup.batches.map((batch) => (
                                                <React.Fragment key={batch._id}>
                                                    <TableRow
                                                        className="cursor-pointer"
                                                        onClick={() => handleExpandBatch(batch._id)}
                                                    >
                                                        <TableCell>
                                                            <span className={cn(
                                                                'inline-flex size-7 items-center justify-center border',
                                                                expandedBatch === batch._id && 'bg-muted'
                                                            )}>
                                                                {expandedBatch === batch._id
                                                                    ? <ChevronUp className="size-4" />
                                                                    : <ChevronDown className="size-4" />}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-foreground">
                                                            {formatTime(batch.createdAt)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="block text-sm font-medium text-foreground">
                                                                {batch.totalJobs}
                                                            </span>
                                                            {batch.sampleProducts.length > 0 && (
                                                                <span className="block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
                                                                    {batch.sampleProducts.slice(0, 3).join(', ')}
                                                                    {batch.sampleProducts.length > 3 && '...'}
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="min-w-[150px]">
                                                            <div className="flex items-center gap-3">
                                                                <Progress
                                                                    value={getProgressPercent(batch)}
                                                                    className={cn(
                                                                        'h-1.5 flex-1',
                                                                        batch.failed > 0
                                                                            ? '[&_[data-slot=progress-indicator]]:bg-destructive'
                                                                            : '[&_[data-slot=progress-indicator]]:bg-success'
                                                                    )}
                                                                />
                                                                <span className="min-w-10 text-xs text-muted-foreground">
                                                                    {Math.round(getProgressPercent(batch))}%
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <TallyBadge kind="success" count={batch.completed} />
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <TallyBadge kind="failed" count={batch.failed} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className={cn('gap-1', statusTone[batch.status])}>
                                                                {batch.status === 'in_progress' ? <Play /> :
                                                                    batch.status === 'success' ? <CircleCheck /> :
                                                                        batch.status === 'failed' ? <CircleAlert /> :
                                                                            <Hourglass />}
                                                                {statusLabels[batch.status] || batch.status}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                                            {batch.status !== 'success' && batch.status !== 'failed' && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon-sm"
                                                                            className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                                                                            onClick={() => setConfirmCancelBatch(batch._id)}
                                                                            disabled={cancellingBatch === batch._id}
                                                                            aria-label={t('PAGES.IMPORT_HISTORY.CANCEL_IMPORT')}
                                                                        >
                                                                            {cancellingBatch === batch._id
                                                                                ? <Loader2 className="size-4 animate-spin" />
                                                                                : <CircleX className="size-4" />}
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{t('PAGES.IMPORT_HISTORY.CANCEL_IMPORT')}</TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Expanded details row */}
                                                    {expandedBatch === batch._id && (
                                                        <TableRow className="hover:bg-transparent">
                                                            <TableCell colSpan={8} className="border-0 bg-muted/50 p-4">
                                                                {loadingDetails === batch._id ? (
                                                                    <div className="flex justify-center py-6">
                                                                        <Loader2 className="size-6 animate-spin text-muted-foreground" />
                                                                    </div>
                                                                ) : batchDetails[batch._id] ? (
                                                                    <div className="overflow-x-auto">
                                                                        <Table>
                                                                            <TableHeader>
                                                                                <TableRow className="hover:bg-transparent">
                                                                                    <TableHead className="w-14" />
                                                                                    <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.PRODUCT')}</TableHead>
                                                                                    <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.SIZE')}</TableHead>
                                                                                    <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.PATTERN')}</TableHead>
                                                                                    <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.OPERATION')}</TableHead>
                                                                                    <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.STATUS')}</TableHead>
                                                                                    <TableHead className={headCls}>{t('PAGES.IMPORT_HISTORY.ERROR')}</TableHead>
                                                                                    <TableHead className={cn(headCls, 'w-16 text-center')}>{t('PAGES.IMPORT_HISTORY.ACTIONS')}</TableHead>
                                                                                </TableRow>
                                                                            </TableHeader>
                                                                            <TableBody>
                                                                                {batchDetails[batch._id].jobs.slice(0, 30).map((job) => (
                                                                                    <TableRow key={job.jobId} className="bg-card">
                                                                                        <TableCell className="w-14">
                                                                                            {job.resultProduct?.thumbnailPath ? (
                                                                                                <img
                                                                                                    src={job.resultProduct.thumbnailPath}
                                                                                                    alt={job.productData?.name || ''}
                                                                                                    className="size-10 border object-cover"
                                                                                                />
                                                                                            ) : (
                                                                                                <span className="flex size-10 items-center justify-center border bg-muted">
                                                                                                    <span className="size-4 bg-border" />
                                                                                                </span>
                                                                                            )}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <span className="block font-medium text-foreground">
                                                                                                {formatProductDetails(job)}
                                                                                            </span>
                                                                                            {getProductSubDetails(job) && (
                                                                                                <span className="block text-xs text-muted-foreground">
                                                                                                    {getProductSubDetails(job)}
                                                                                                </span>
                                                                                            )}
                                                                                        </TableCell>
                                                                                        <TableCell className="text-muted-foreground">
                                                                                            {job.productData?.sizeName || '-'}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            {job.productData?.pattern ? (
                                                                                                <Badge variant="outline" className="capitalize text-muted-foreground">
                                                                                                    {job.productData.pattern}
                                                                                                </Badge>
                                                                                            ) : (
                                                                                                <span className="text-muted-foreground">-</span>
                                                                                            )}
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Badge variant="outline" className="capitalize text-muted-foreground">
                                                                                                {job.operation}
                                                                                            </Badge>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <div className="flex items-center gap-2">
                                                                                                {(job.status === 'pending' || job.status === 'processing' || job.status === 'generating_seamless') && (
                                                                                                    <Loader2 className={cn('size-3 animate-spin', statusTone[job.status])} />
                                                                                                )}
                                                                                                <Badge variant="outline" className={cn('capitalize', statusTone[job.status])}>
                                                                                                    {job.status.replace('_', ' ')}
                                                                                                </Badge>
                                                                                            </div>
                                                                                        </TableCell>
                                                                                        <TableCell
                                                                                            className="max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap text-destructive"
                                                                                            title={job.errorDetails?.userMessage || job.error}
                                                                                        >
                                                                                            {job.errorDetails?.userMessage || job.error || '-'}
                                                                                        </TableCell>
                                                                                        <TableCell className="text-center">
                                                                                            {job.status === 'failed' && (
                                                                                                <Tooltip>
                                                                                                    <TooltipTrigger asChild>
                                                                                                        <Button
                                                                                                            variant="ghost"
                                                                                                            size="icon-sm"
                                                                                                            onClick={(e) => { e.stopPropagation(); handleRetryJob(job.jobId, batch._id); }}
                                                                                                            disabled={retryingJob === job.jobId}
                                                                                                            aria-label={t('PAGES.IMPORT_HISTORY.RETRY')}
                                                                                                        >
                                                                                                            {retryingJob === job.jobId
                                                                                                                ? <Loader2 className="size-4 animate-spin" />
                                                                                                                : <RotateCcw className="size-4" />}
                                                                                                        </Button>
                                                                                                    </TooltipTrigger>
                                                                                                    <TooltipContent>{t('PAGES.IMPORT_HISTORY.RETRY')}</TooltipContent>
                                                                                                </Tooltip>
                                                                                            )}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                                {batchDetails[batch._id].jobs.length > 30 && (
                                                                                    <TableRow className="hover:bg-transparent">
                                                                                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                                                                                            + {batchDetails[batch._id].jobs.length - 30} {t('PAGES.IMPORT_HISTORY.MORE_PRODUCTS')}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                )}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </div>
                                                                ) : (
                                                                    <p className="py-4 text-center text-xs text-muted-foreground">
                                                                        {t('PAGES.IMPORT_HISTORY.NO_DETAILS')}
                                                                    </p>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    ))}

                    {/*
                      Pagination, by hand. MUI's TablePagination has no shadcn counterpart,
                      and the one in ui/pagination.tsx is link-based — wrong for a control
                      that only moves state.
                    */}
                    <div className="flex flex-wrap items-center justify-end gap-4 rounded-xl border bg-card p-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                                {t('PAGES.MY_PRODUCTS.PAGINATION.ROWS_PER_PAGE')}
                            </span>
                            <Select
                                value={String(rowsPerPage)}
                                onValueChange={(value) => handleChangeRowsPerPage({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
                            >
                                <SelectTrigger size="sm" className="w-20">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROWS_PER_PAGE_OPTIONS.map(option => (
                                        <SelectItem key={option} value={String(option)}>
                                            {option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <span className="text-xs text-muted-foreground">
                            {firstRow}-{lastRow} {t('PAGES.MY_PRODUCTS.PAGINATION.OF')} {totalBatches}
                        </span>

                        <div className="flex gap-1.5">
                            <Button
                                variant="outline"
                                size="icon-sm"
                                disabled={page === 0}
                                onClick={() => handleChangePage(null, page - 1)}
                                aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.PREVIOUS', 'Previous page')}
                            >
                                <ChevronLeft className="size-4 rtl:rotate-180" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon-sm"
                                disabled={page >= pageCount - 1}
                                onClick={() => handleChangePage(null, page + 1)}
                                aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.NEXT', 'Next page')}
                            >
                                <ChevronRight className="size-4 rtl:rotate-180" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel confirmation dialog */}
            <Dialog open={!!confirmCancelBatch} onOpenChange={(open) => { if (!open) setConfirmCancelBatch(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('PAGES.IMPORT_HISTORY.CANCEL_CONFIRM_TITLE')}</DialogTitle>
                        <DialogDescription>{t('PAGES.IMPORT_HISTORY.CANCEL_CONFIRM_DESC')}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmCancelBatch(null)}>
                            {t('PAGES.IMPORT_HISTORY.GO_BACK')}
                        </Button>
                        <Button
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                            onClick={handleCancelBatch}
                        >
                            {t('PAGES.IMPORT_HISTORY.CANCEL_IMPORT')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}

export default ImportHistoryPage;
